<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1000"
  >
    <!--begin::Modal dialog-->
    <div class="bg-white poppins">
      <!--begin::Modal header-->
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ $t("create_pickup") }}</h4>
        <!--begin::Close-->
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
        <!--end::Close-->
      </div>
      <!--begin::Modal header-->
      <!--begin::Modal body-->
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <!--begin::Body-->
        <form id="createForm" class="pa-3 mx-3 row elevation-0">
          <div class="col-12 h-75px">
            <v-combobox
              v-model="formData.order_ids"
              :label="$t('orders')"
              outlined
              dense
              @change="handleChangeMultiple"
              multiple
              deletable-chips
              clearable
              allow-overflow
              small-chips
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-select
              v-model="formData.collection_address_id"
              :label="$t('collection_address')"
              item-text="text"
              item-value="index"
              :items="serverData?.pickup_collection_addresses"
              clearable
              outlined
              dense
              :error-messages="collectionAddressErrors"
              @input="$v.formData.collection_address_id.$touch()"
              @blur="$v.formData.collection_address_id.$touch()"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4">
            <v-menu
              v-model="menu1"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formData.collection_date"
                  :label="$t('collection_date')"
                  readonly
                  clearable
                  dense
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  :error-messages="collection_dateErrors"
                  @input="$v.formData.collection_date.$touch()"
                  @blur="$v.formData.collection_date.$touch()"
                />
              </template>
              <v-date-picker
                v-model="formData.collection_date"
                :min="new Date().toISOString().split('T')[0]"
                @input="menu1 = false"
              />
            </v-menu>
          </div>

          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-select
              v-model="formData.package_type"
              :label="$t('package_type')"
              item-text="text"
              item-value="index"
              :items="serverData?.pickup_package_types"
              clearable
              outlined
              dense
              :error-messages="packageTypeErrors"
              @input="$v.formData.package_type.$touch()"
              @blur="$v.formData.package_type.$touch()"
            />
          </div>

          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-select
              v-model="formData.vehicle_type_id"
              :label="$t('vehicle_type')"
              :items="serverData?.pickup_vehicle_types"
              item-text="text"
              item-value="index"
              clearable
              outlined
              dense
              :error-messages="vehicleTypeErrors"
              @input="$v.formData.vehicle_type_id.$touch()"
              @blur="$v.formData.vehicle_type_id.$touch()"
            />
          </div>

          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-text-field
              v-model.number="formData.number_of_packages"
              :label="number_of_packages_label"
              type="number"
              :min="0"
              clearable
              outlined
              dense
              @change="() => validateMinValue('number_of_packages', 0)"
              :error-messages="number_of_packagesErrors"
              @input="$v.formData.number_of_packages.$touch()"
              @blur="$v.formData.number_of_packages.$touch()"
            />
          </div>

          <div
            class="col-12 col-sm-6 col-md-4"
            :class="{ 'col-md-4': formData.package_type === 1 }"
          >
            <v-text-field
              v-model.number="formData.weight"
              type="number"
              :min="0"
              :label="$t('weight')"
              clearable
              outlined
              dense
              @change="() => validateMinValue('weight', 0)"
              :error-messages="weightErrors"
              @input="$v.formData.weight.$touch()"
              @blur="$v.formData.weight.$touch()"
            />
          </div>
          <div
            class="col-12 col-sm-6 col-md-4"
            :class="{ 'col-md-4': formData.package_type === 1 }"
          >
            <v-text-field
              v-model.number="formData.volumetric_weight"
              type="number"
              :min="0"
              :label="$t('volumetric_weight')"
              clearable
              outlined
              dense
              @change="() => validateMinValue('volumetric_weight', 0)"
            />
          </div>
          <div
            class="col-12 col-sm-6 col-md-4"
            :class="{ 'col-md-4': formData.package_type === 1 }"
          >
            <v-text-field
              v-model="formData.comment"
              :label="$t('comment')"
              clearable
              outlined
              dense
            />
          </div>
        </form>
        <!--end::Body-->

        <!--begin::Actions-->
        <div class="my-2 d-flex flex-grow-1 align-end justify-center">
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submit"
          >
            Submit
          </button>
        </div>
        <!--end::Actions-->
      </div>
      <!--end::Modal body-->
    </div>
    <!--end::Modal dialog-->
  </v-dialog>
</template>

<script>
import validationMixin from "@/own/mixins/validationMixin";
import { required } from "vuelidate/lib/validators";
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import { getToken } from "@/core/services/jwt.service";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";

export default {
  name: "CreatePickup",
  mixins: [validationMixin, fieldValueValidation],
  props: ["selected", "updateTableContent", "pageLoader"],
  validations() {
    return {
      formData: {
        number_of_packages: { required },
        collection_date: { required },
        package_type: { required },
        collection_address_id: { required },
        vehicle_type_id: { required },
        weight: { required },
      },
    };
  },
  data: () => ({
    dialog: false,
    serverData: null,
    menu1: false,
    formData: {
      order_ids: [],
      vehicle_type_id: null,
      collection_date: null,
      package_type: null,
      collection_address_id: null,
      number_of_packages: null,
      weight: null,
      volumetric_weight: null,
    },
  }),
  methods: {
    async toggleModal() {
      this.reset();
      if (!this.dialog) {
        await this.loadDataFromServer();
        this.setOrders();
      }
      this.dialog = !this.dialog;
    },
    handleChangeMultiple(val) {
      let seperated = [];
      val.forEach((element) => {
        let subElement = element.trim().split(/(?:,| )+/);
        subElement.forEach((sub) => {
          seperated.push(sub);
        });
      });
      this.formData.order_ids = seperated;
    },

    createNewItem() {
      this.$nextTick(() => {
        this.$refs.collectionAddressComponent.toggleModal();
      });
    },
    async getData(value) {
      let data = { q: value };
      // if (this.userId) data.client = this.userId;
      if (this.customer_id) data.client = this.customer_id;

      await fetch(`${process.env.VUE_APP_BASE_URL}/orders/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => {
          this.entries = res.orders;
        })
        .finally(() => (this.isLoading = false));
    },
    setOrders() {
      this.formData.order_ids = this.selected.map((item) => item.systemId);
    },
    setDataOfAddress(data, type) {
      this.formData[`${type}`] = data;
    },
    reset() {
      this.$v.$reset();
      this.formData = {
        order_ids: [],
        vehicle_type_id: null,
        collection_date: null,
        package_type: null,
        collection_address_id: null,
        number_of_packages: null,
        weight: null,
        volumetric_weight: null,
      };
    },
    async loadDataFromServer() {
      this.pageLoader(true);
      await ApiService.post("/fulfillment/orders/order_pickup_data")
        .then((response) => {
          this.serverData = response.data;
          this.pageLoader(false);
        })
        .catch(() => {
          this.dialog = false;
          this.pageLoader(false);
        });
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      if (!this.formData.order_ids.length) {
        Swal.fire({
          title: "Info",
          text: `Orders are required!`,
          icon: "warning",
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      this.pageLoader(true);

      const data = {
        ...this.formData,
        orders: [...this.formData.order_ids],
      };
      // const [year, month, day] = this.formData.collection_date.split("-");
      // data.collection_date = `${year}-${month}-${day}`;

      ApiService.post(`fulfillment/orders/order_pickup_create`, data)
        .then((res) => {
          Swal.fire({
            title: "Success",
            html: `Updated successfully`,
            icon: "success",
            showConfirmButton: true,
            // timer: 2500,
          });
          this.toggleModal();
          this.updateTableContent();
        })
        .catch(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
    // formatDate(date) {
    //   if (!date) return null;
    //
    //   const [year, month, day] = date.split("-");
    //   return `${day}-${month}-${year}`;
    // },
  },
  computed: {
    warehouses: function () {
      return this.$store.getters.getORDERSWarehouses;
    },
    number_of_packages_label() {
      const labels = {
        1: this.$t("number_of_pallets"),
        2: this.$t("number_of_boxes"),
        3: this.$t("number_of_pouches"),
      };
      return (
        labels[this.formData.package_type] || this.$t("number_of_packages")
      );
    },
    collection_dateErrors() {
      return this.handleFormValidation("collection_date", this);
    },
    // computedDateFormatted() {
    //   return this.formatDate(this.formData.collection_date);
    // },
    packageTypeErrors() {
      return this.handleFormValidation("package_type", this);
    },
    collectionAddressErrors() {
      return this.handleFormValidation("collection_address_id", this);
    },
    number_of_packagesErrors() {
      return this.handleFormValidation("number_of_packages", this);
    },
    vehicleTypeErrors() {
      return this.handleFormValidation("vehicle_type_id", this);
    },
    weightErrors() {
      return this.handleFormValidation("weight", this);
    },
  },
  watch: {
    search(val) {
      clearTimeout(this.typingTimer);
      this.isLoading = true;
      if (val) {
        this.typingTimer = setTimeout(() => {
          this.getData(val.trim());
        }, this.doneTypingInterval);
      } else {
        this.isLoading = false;
        return;
      }
    },
  },
};
</script>
